import {PrismicNextImage} from '@prismicio/next/pages'
import {PrismicRichText} from '@prismicio/react'
import type {ProductOverviewSliceProductBenefits} from 'prismicio-types'
import {Container} from 'src/common/Container'
import {Gradient} from './components/separators'

type ProductBenefitsVariationProps = {
  slice: ProductOverviewSliceProductBenefits & {
    slice_type: string
    variation: string
  }
}

export const ProductOverviewProductBenefitsVariation = ({slice}: ProductBenefitsVariationProps) => (
  <Container
    data-slice-type={slice.slice_type}
    data-slice-variation={slice.variation}
    className="flex flex-col items-center gap-0 !pb-0 !pt-10 md:!pt-12 lg:!pt-20"
    backgroundColor={slice.primary.background_color || 'Grey/100'}
  >
    <div
      className="mx-auto flex w-full max-w-[1200px] flex-col gap-6 px-5 md:px-10 lg:px-5 xl:px-0"
      data-aos="fade-up"
      data-aos-offset="0"
    >
      <PrismicRichText
        field={slice.primary.tag}
        components={{
          paragraph: ({children}) => (
            <p className="text-[12px] font-semibold uppercase leading-[100%] tracking-[0.72px] text-moss-blue">
              {children}
            </p>
          ),
          strong: ({children}) => <strong className="font-semibold text-moss-blue">{children}</strong>,
        }}
      />
      <div>
        <PrismicRichText
          field={slice.primary.heading}
          components={{
            heading1: ({children}) => (
              <h2 className="text-[32px] font-medium leading-[normal] text-moss-black md:text-[42px]">{children}</h2>
            ),
            strong: ({children}) => <span className="text-moss-grey-400">{children}</span>,
          }}
        />
      </div>
    </div>

    <div
      data-aos="fade-up"
      data-aos-offset="0"
      className="
          relative
          isolate
          mt-0
          grid
          w-full
          max-w-[1440px]
          grid-cols-[20px_1fr_20px]
          grid-rows-[80px_repeat(4,auto)_80px]
          md:grid-cols-[40px_repeat(2,1fr)_40px]
          md:grid-rows-[80px_repeat(2,auto)_80px]
          lg:grid-cols-[120px_repeat(4,1fr)_120px]
          lg:grid-rows-[80px_auto_80px]
          "
    >
      <Gradient color="247, 247, 247" gradientStart={20} height={64} location="top" />

      {/* Top decorative row */}
      <TopSeparators />

      {/* Content cards */}
      {slice.primary.grid_elements.map((content, index) => (
        <div
          key={`benefit-${index}`}
          className="
              col-start-2
              ml-[-1px]
              mt-[-1px]
              flex
              h-[272px]
              flex-col
              gap-6
              rounded-[4px]
              border
              border-moss-grey-150
              p-6
              md:col-auto
            "
        >
          <div className="relative flex size-12 items-center justify-center rounded-[4px] bg-green-neon">
            <PrismicNextImage field={content.icon} alt="" className="size-6" />
            <div className="absolute inset-0 rounded-[4px] border border-black/[10%]" />
          </div>
          <div className="flex flex-col gap-4">
            <h4 className="text-[24px] font-medium leading-[normal] text-moss-black">{content.title}</h4>
            <p className="text-tg-body-m text-moss-grey-550">{content.description}</p>
          </div>
        </div>
      ))}

      {/* Bottom decorative row */}
      <BottomSeparators />

      {/* Side separators */}
      <SideSeparators />

      <Gradient color="247, 247, 247" gradientStart={20} height={64} location="bottom" />
    </div>
  </Container>
)

const TopSeparators = () => (
  <>
    {/* Top left corner */}
    <div className="col-start-1 row-start-1 rounded-br-[4px] border-b border-r border-moss-grey-150" />
    {/* Top center cells - mobile: 1 cell, tablet: 2 cells, desktop: 4 cells */}
    <div className="col-start-2 row-start-1 ml-[-1px] rounded-b-[4px] border-x border-b border-moss-grey-150 md:hidden" />
    <div className="col-start-2 row-start-1 ml-[-1px] hidden rounded-b-[4px] border-x border-b border-moss-grey-150 md:block lg:hidden" />
    <div className="col-start-3 row-start-1 ml-[-1px] hidden rounded-b-[4px] border-x border-b border-moss-grey-150 md:block lg:hidden" />
    <div className="col-start-2 row-start-1 ml-[-1px] hidden rounded-b-[4px] border-x border-b border-moss-grey-150 lg:block" />
    <div className="col-start-3 row-start-1 ml-[-1px] hidden rounded-b-[4px] border-x border-b border-moss-grey-150 lg:block" />
    <div className="col-start-4 row-start-1 ml-[-1px] hidden rounded-b-[4px] border-x border-b border-moss-grey-150 lg:block" />
    <div className="col-start-5 row-start-1 ml-[-1px] hidden rounded-b-[4px] border-x border-b border-moss-grey-150 lg:block" />
    {/* Top right corner */}
    <div className="col-start-3 row-start-1 ml-[-1px] rounded-bl-[4px] border-b border-l border-moss-grey-150 md:col-start-4 lg:col-start-6" />
  </>
)

const BottomSeparators = () => (
  <>
    {/* Bottom left corner - mobile: row 6, tablet: row 4, desktop: row 3 */}
    <div className="col-start-1 row-start-6 mt-[-1px] rounded-tr-[4px] border-r border-t border-moss-grey-150 md:row-start-4 lg:row-start-3" />
    {/* Bottom center cells - mobile: 1 cell, tablet: 2 cells, desktop: 4 cells */}
    <div className="col-start-2 row-start-6 ml-[-1px] mt-[-1px] rounded-t-[4px] border-x border-t border-moss-grey-150 md:row-start-4 md:hidden lg:row-start-3" />
    <div className="col-start-2 row-start-4 ml-[-1px] mt-[-1px] hidden rounded-t-[4px] border-x border-t border-moss-grey-150 md:block lg:hidden" />
    <div className="col-start-3 row-start-4 ml-[-1px] mt-[-1px] hidden rounded-t-[4px] border-x border-t border-moss-grey-150 md:block lg:hidden" />
    <div className="col-start-2 row-start-3 ml-[-1px] mt-[-1px] hidden rounded-t-[4px] border-x border-t border-moss-grey-150 lg:block" />
    <div className="col-start-3 row-start-3 ml-[-1px] mt-[-1px] hidden rounded-t-[4px] border-x border-t border-moss-grey-150 lg:block" />
    <div className="col-start-4 row-start-3 ml-[-1px] mt-[-1px] hidden rounded-t-[4px] border-x border-t border-moss-grey-150 lg:block" />
    <div className="col-start-5 row-start-3 ml-[-1px] mt-[-1px] hidden rounded-t-[4px] border-x border-t border-moss-grey-150 lg:block" />
    {/* Bottom right corner */}
    <div className="col-start-3 row-start-6 ml-[-1px] mt-[-1px] rounded-tl-[4px] border-l border-t border-moss-grey-150 md:col-start-4 md:row-start-4 lg:col-start-6 lg:row-start-3" />
  </>
)

const SideSeparators = () => (
  <>
    {/* Left side separators */}
    <div className="col-start-1 row-start-2 mt-[-1px] rounded-r-[4px] border-y border-r border-moss-grey-150" />
    <div className="col-start-1 row-start-3 mt-[-1px] rounded-r-[4px] border-y border-r border-moss-grey-150" />
    <div className="col-start-1 row-start-4 mt-[-1px] rounded-r-[4px] border-y border-r border-moss-grey-150 md:hidden" />
    <div className="col-start-1 row-start-5 mt-[-1px] rounded-r-[4px] border-y border-r border-moss-grey-150 md:hidden" />

    {/* Right side separators */}
    <div className="col-start-3 row-start-2 ml-[-1px] mt-[-1px] rounded-l-[4px] border-y border-l border-moss-grey-150 md:col-start-4 lg:col-start-6" />
    <div className="col-start-3 row-start-3 ml-[-1px] mt-[-1px] rounded-l-[4px] border-y border-l border-moss-grey-150 md:col-start-4 lg:col-start-6 lg:hidden" />
    <div className="col-start-3 row-start-4 ml-[-1px] mt-[-1px] rounded-l-[4px] border-y border-l border-moss-grey-150 md:hidden" />
    <div className="col-start-3 row-start-5 ml-[-1px] mt-[-1px] rounded-l-[4px] border-y border-l border-moss-grey-150 md:hidden" />
  </>
)
